import { UserIcon } from '@heroicons/react/16/solid';
import { useAsyncList } from '@react-stately/data';
import React, { Key, useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { format } from 'date-fns';
import isFileImage from 'src/features/product/product-detail/utils/isFileImage';
import { AppImage } from 'src/shared/components';
import { ROUTE_PATHS } from 'src/shared/config/routes';
import { ExampleItem } from 'src/shared/types/exampleItem';
import isValidDate from 'src/shared/utils/isValidDate';
import toReadableLabel from 'src/shared/utils/toReadableLabel';

import {
  Chip,
  SortDescriptor,
  Spinner,
  useDisclosure
} from 'src/shared/components/ui';
import {
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Typography
} from 'src/shared/components/ui';

import samplingExampleItemsTableColumns from '../../constants/samplingsExampleItemsTableColumns';
import getExampleItemCreator from '../../utils/getExampleItemCreator';
import getExampleItemStageLabel from '../../utils/getExampleItemStageLabel';
import SamplingExampleItemModal from '../sampling-example-item-modal/SamplingExampleItemModal';

const getExampleItemsByFilter = (
  exampleItems: ExampleItem[],
  filterText: string | undefined
) => {
  const nonArchivedExampleItems = exampleItems?.filter(
    (exampleItem: ExampleItem) => !exampleItem.archived
  );

  if (!filterText) return nonArchivedExampleItems;
  if (filterText === 'all') return nonArchivedExampleItems;

  return nonArchivedExampleItems?.filter((exampleItem: ExampleItem) => {
    if (exampleItem?.producer_supplier_cloak) {
      return exampleItem?.producer_supplier_cloak === filterText;
    }

    return false;
  });
};

interface SamplingExampleItemsTableProps {
  exampleItems: ExampleItem[];
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  selectedSupplier: string;
}

const SamplingExampleItemsTable: React.FC<SamplingExampleItemsTableProps> = ({
  exampleItems,
  isLoading,
  setIsLoading,
  selectedSupplier
}) => {
  const { id } = useParams<{ id: string }>();
  const { isOpen, onOpenChange, onOpen } = useDisclosure();
  const [selectedExampleItem, setSelectedExampleItem] =
    useState<ExampleItem | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const exampleItemId = searchParams.get('example-item');

  useEffect(() => {
    if (exampleItems.length > 0 && exampleItemId) {
      const exampleItem = exampleItems.find(
        (item) => item.id === exampleItemId
      );

      if (exampleItem) {
        setSelectedExampleItem(exampleItem);
        if (!isOpen) {
          onOpen();
        }
      }
    } else if (!exampleItemId && isOpen) {
      onOpenChange();
    }
  }, [exampleItems, exampleItemId, isOpen, onOpen, onOpenChange]);

  const handleModalClose = (newIsOpen: boolean) => {
    if (!newIsOpen && exampleItemId) {
      const newSearchParams = new URLSearchParams(searchParams);

      newSearchParams.delete('example-item');
      setSearchParams(newSearchParams, { replace: true });
    }
    if (newIsOpen) {
      onOpen();
    } else {
      onOpenChange();
    }
  };

  const list = useAsyncList({
    async load() {
      setIsLoading(false);
      const filteredExampleItems = getExampleItemsByFilter(
        exampleItems,
        selectedSupplier
      );

      return {
        items: filteredExampleItems
      };
    },
    async sort({ items, sortDescriptor, filterText }) {
      if (!sortDescriptor.direction) {
        const filteredExampleItems = getExampleItemsByFilter(
          exampleItems,
          filterText
        );

        return {
          items: [...filteredExampleItems]
        };
      }

      return {
        items: items.sort((a: any, b: any) => {
          let first = a[sortDescriptor.column];
          let second = b[sortDescriptor.column];

          if (first == null) return 1;
          if (second == null) return -1;

          let cmp: number;

          if (
            typeof first === 'string' &&
            typeof second === 'string' &&
            isValidDate(first) &&
            isValidDate(second)
          ) {
            cmp = new Date(first).getTime() - new Date(second).getTime();
          } else {
            const firstNum = Number(first);
            const secondNum = Number(second);

            if (!isNaN(firstNum) && !isNaN(secondNum)) {
              cmp = (firstNum || 0) - (secondNum || 0);
            } else {
              cmp = String(first).localeCompare(String(second), undefined, {
                sensitivity: 'base'
              });
            }
          }

          if (sortDescriptor.direction === 'descending') {
            cmp *= -1;
          }

          return cmp;
        })
      };
    }
  });

  useEffect(() => {
    list.setFilterText(selectedSupplier);
  }, [selectedSupplier, exampleItems]);

  const handleSortChange = (descriptor: SortDescriptor) => {
    if (
      list.sortDescriptor?.column === descriptor.column &&
      list.sortDescriptor?.direction === 'descending'
    ) {
      list.sort({ column: undefined, direction: undefined } as any);
    } else {
      list.sort(descriptor);
    }
  };

  const renderCell = useCallback((item: any, columnKey: Key) => {
    switch (columnKey) {
      case 'images':
        const images = item?.uploaded_files?.filter(isFileImage);

        return (
          <div className="flex items-center gap-2">
            <AppImage
              id={item?.id}
              className="size-16 object-cover min-w-16 rounded-xl bg-gray-2 border border-gray-4"
              src={images?.[0]?.url}
            />
            <AppImage
              id={item?.id}
              className="size-16 object-cover min-w-16 rounded-xl bg-gray-2 border border-gray-4"
              src={images?.[1]?.url}
            />
          </div>
        );

      case 'name':
        return (
          <div className="flex items-center gap-2">
            <Typography variant="body-2" weight="medium">
              {item.staffOnly && (
                <span
                  className="text-p-cavela-blue"
                  title="Only impersonating staff can see this"
                >
                  🕵🏻
                </span>
              )}
              {item.name ?? '-'}
            </Typography>
          </div>
        );

      case 'example_item_type':
        return (
          <Chip className="bg-white border border-gray-4 rounded-md font-medium">
            {toReadableLabel(item?.example_item_type)}
          </Chip>
        );

      case 'creator':
        return getExampleItemCreator(item);

      case 'stage':
        return getExampleItemStageLabel(item?.stage);

      case 'supplier_name':
        return (
          <div className="flex items-center gap-1">
            <Typography variant="body-2" className="flex items-center">
              <UserIcon className="size-4 mr-1" />{' '}
              {item?.producer_supplier_cloak}
            </Typography>
          </div>
        );

      case 'requested_at':
        return (
          <Typography variant="body-2">
            {format(item.requested_at, 'MMM dd, yyyy')}
          </Typography>
        );

      default:
        return '';
    }
  }, []);

  return (
    <div className="flex flex-col">
      <Table
        aria-label="Example items table"
        sortDescriptor={list?.sortDescriptor}
        onSortChange={handleSortChange}
      >
        <TableHeader columns={samplingExampleItemsTableColumns}>
          {(column) => (
            <TableColumn
              allowsSorting={column.allowsSorting}
              className="px-4"
              key={column.key}
            >
              {column.label}
            </TableColumn>
          )}
        </TableHeader>
        <TableBody
          emptyContent={
            isLoading ? (
              <Spinner size="lg" />
            ) : (
              list?.items?.length === 0 && 'No references'
            )
          }
          items={list?.items ?? []}
          isLoading={isLoading}
        >
          {(item) => (
            <TableRow key={item.id}>
              {(columnKey) => (
                <TableCell>
                  <Link
                    className="!h-24 flex items-center"
                    to={`${ROUTE_PATHS.PRODUCTS}/${id}?tab=samples&example-item=${item.id}`}
                  >
                    <div className="truncate">
                      {renderCell(item, columnKey)}
                    </div>
                  </Link>
                </TableCell>
              )}
            </TableRow>
          )}
        </TableBody>
      </Table>

      <SamplingExampleItemModal
        isOpen={isOpen}
        onOpenChange={handleModalClose}
        exampleItem={selectedExampleItem}
      />
    </div>
  );
};

export default SamplingExampleItemsTable;
